// Expand a weekly-plan CONCEPT slot into a full recipe — in the
// BACKGROUND, via the same stub pipeline as normal AI drafts
// (backgroundRecipeGen: server-side persistence under
// EdgeRuntime.waitUntil, 150s timeout, failed → Retry state).
//
// No-waiting-page-of-death rule: the caller never awaits generation.
// This mints a pending user_recipes stub, re-points the scheduled
// meal at it (clearing the concept snapshot so the drawer resolves
// the live stub row), and fires the detached run. The drawer shows a
// "writing this recipe" state that flips live via the user_recipes
// realtime subscription; closing it any time loses nothing.
//
// Paid + explicit: only fires from the GENERATE FULL RECIPE CTA
// (generate-recipe:final, 10 tokens).

import { buildAIContext } from "./aiContext";
import { runBackgroundRecipeGen } from "./backgroundRecipeGen";

export async function startConceptExpansion({
  meal,
  pantry = [],
  profile = null,
  ingredientInfo = null,
  saveRecipe,
  updateMeal,
}) {
  const snapshot = meal?.recipe_snapshot;
  const c = snapshot?._concept;
  if (!c) throw new Error("Not a concept meal");
  if (typeof saveRecipe !== "function" || typeof updateMeal !== "function") {
    throw new Error("startConceptExpansion needs saveRecipe and updateMeal");
  }

  // Star the concept's estimated ingredients the user actually owns
  // (cap 3) so the full recipe builds around what's on hand.
  const pantryIds = new Set();
  for (const p of pantry) {
    if (p?.canonicalId) pantryIds.add(p.canonicalId);
    if (p?.ingredientId) pantryIds.add(p.ingredientId);
  }
  const starIngredientIds = (snapshot.ingredients || [])
    .map((i) => i?.ingredientId)
    .filter((id) => id && pantryIds.has(id))
    .slice(0, 3);

  const priority = c.priorityMode || "chefs_choice";
  const ai = buildAIContext({
    pantry, profile, ingredientInfo,
    mode: "rich",
    starIngredientIds,
    priority,
  });

  const stubMeta = {
    title: snapshot.title || "New recipe",
    subtitle: snapshot.subtitle || "",
    emoji: "🍳",
  };
  const stub = await saveRecipe(
    {
      ...stubMeta,
      ingredients: [],
      steps: [],
      serves: meal.servings || snapshot.serves || undefined,
      _gen: "pending",
      _genStartedAt: new Date().toISOString(),
    },
    "ai",
  );
  if (!stub?.id || !stub?.slug) throw new Error("Couldn't start the recipe — try again.");

  // Re-point the meal at the stub NOW and clear the concept snapshot —
  // from here the drawer/calendar resolve the live user_recipes row,
  // which flips from pending → finished via realtime. updateMeal also
  // re-syncs prep notifications once real prepSteps exist (they don't
  // yet; the next meal edit or reschedule picks them up).
  const updated = await updateMeal(meal.id, {
    recipe_slug: stub.slug,
    recipe_user_id: stub.userId || meal.user_id,
    recipe_snapshot: null,
  });

  // Detached — nothing awaits this. Server persists even if the app
  // backgrounds mid-generation.
  runBackgroundRecipeGen({
    stubId: stub.id,
    payload: {
      pantry: ai.pantry,
      prefs: {
        mealPrompt: snapshot.subtitle
          ? `${snapshot.title} — ${snapshot.subtitle}`
          : snapshot.title,
        priority,
        mealTiming: c.slot || "any",
        starIngredientIds,
        ...(snapshot.cuisine ? { cuisine: snapshot.cuisine } : {}),
      },
      context: ai.context,
    },
    finalizeInputs: { userId: stub.userId || meal.user_id },
    stubMeta,
  });

  return updated;
}
